import { css, keyframes } from '@emotion/react';
import { styled } from '@sds-system/system';
import { getAllStyledProps } from '@sds-system/utils';
import type {
  PositionX,
  PositionY,
  StyledToastRootProps,
  StyledToastWrapperProps,
  ToastPosition,
} from './Toast.types';

export const toastAnimation = (x: PositionX, y: PositionY) => {
  if (x === 'center' && y === 'center') {
    return;
  }

  return keyframes`

      from{
        transform: translate(${x === 'center' ? 0 : x === 'left' ? '-100%' : '100%'}, ${x === 'center' && y === 'top' ? '-100%' : x === 'center' && y === 'bottom' ? '100%' : 0})  ;
      }

      to{
        transform: translate(0)
      }

    `;
};

export const ToastOverlay = styled('div')<{ position: ToastPosition }>(({ position }) => {
  const positionY = position.split('-')[0] || 'center';
  const positionX = position.split('-')[1] || 'center';

  return {
    position: 'fixed',
    top: 16,
    left: 16,
    right: 16,
    bottom: 16,
    display: 'flex',
    justifyContent:
      positionX === 'center' ? 'center' : positionX === 'left' ? 'flex-start' : 'flex-end',
    alignItems: positionY === 'center' ? 'center' : positionY === 'top' ? 'flex-start' : 'flex-end',
    zIndex: 9999,
    pointerEvents: 'none',
  };
});

export const ToastRoot = styled('div')<StyledToastRootProps & { toastPosition: ToastPosition }>(
  ({ toastPosition }) => {
    const positionY = (toastPosition.split('-')[0] as PositionY) || 'center';
    const positionX = (toastPosition.split('-')[1] as PositionX) || 'center';
    const animationStyles = toastAnimation(positionX, positionY);

    return css`
      animation: ${animationStyles} ${animationStyles ? '0.2s ease-in-out forwards' : ''};
      opacity: 1;
      transition: opacity 0.1s ease-in-out;
      pointer-events: auto;

      &.remove {
        opacity: 0;
      }
    `;
  },
  getAllStyledProps,
);

export const ToastBoxWrapper = styled('div')<StyledToastWrapperProps>(({ theme }) => {
  return css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding: 20px;
    width: 120px;
    height: 120px;
    background-color: ${theme.palette.bg.dimmer[80]};
    border-radius: ${theme.borderRadius[8]}px;
  `;
}, getAllStyledProps);

export const ToastTextWrapper = styled('div')<StyledToastWrapperProps>(({ theme }) => {
  return css`
    display: flex;
    gap: 12px;
    padding: 16px;
    max-width: 320px;
    background-color: ${theme.palette.bg.dimmer[80]};
    border-radius: ${theme.borderRadius[8]}px;

    & > button {
      flex-shrink: 0;
      color: ${theme.palette.text.interactive.inverse};
    }
  `;
}, getAllStyledProps);
